import { useState, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { Upload, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { processBulkOrders, ParsedOrder } from "@/lib/bulkOrderUtils";
import * as XLSX from 'xlsx';

interface ExcelUploadTabProps {
  onParsed: (orders: ParsedOrder[], summary: any) => void;
  onProgress?: (progress: { completed: number; total: number }) => void;
  onContentChange?: (hasContent: boolean) => void;
}

export interface ExcelUploadTabRef {
  hasContent: () => boolean;
  getEstimatedCount: () => number | undefined;
  parse: () => Promise<void>;
  cancel: () => void;
}

// Match SmartPasteTab timeout for consistency
const CLIENT_TIMEOUT_MS = 90000; // 90 seconds
const LARGE_FILE_SIZE_BYTES = 500 * 1024; // 500KB

export const ExcelUploadTab = forwardRef<ExcelUploadTabRef, ExcelUploadTabProps>(
  ({ onParsed, onProgress, onContentChange }, ref) => {
    const [file, setFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedText, setParsedText] = useState<string>("");
    const abortControllerRef = useRef<AbortController | null>(null);
    const { toast } = useToast();
    const { t } = useLanguage();

    // Pre-parse Excel to text when file is selected (for content check)
    useEffect(() => {
      if (file) {
        parseExcelToText(file).then((text) => {
          setParsedText(text);
          onContentChange?.(text.length > 0);
        }).catch(() => {
          setParsedText("");
          onContentChange?.(false);
        });
      } else {
        setParsedText("");
        onContentChange?.(false);
      }
    }, [file, onContentChange]);

    // Expose imperative methods to parent
    useImperativeHandle(ref, () => ({
      hasContent: () => !!file && parsedText.length > 0,
      getEstimatedCount: () => {
        if (!parsedText) return undefined;
        // Estimate based on newlines (rows)
        const lines = parsedText.split('\n').filter(l => l.trim()).length;
        return Math.max(1, lines - 1); // Subtract header row
      },
      parse: handleParse,
      cancel: () => {
        abortControllerRef.current?.abort();
      },
    }));

    const handleDrag = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === "dragenter" || e.type === "dragover") {
        setDragActive(true);
      } else if (e.type === "dragleave") {
        setDragActive(false);
      }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const droppedFile = e.dataTransfer.files?.[0];
      if (droppedFile && isValidSpreadsheetFile(droppedFile)) {
        setFile(droppedFile);
      } else {
        toast({
          title: t("bulk.errors.invalidFile"),
          description: t("bulk.errors.invalidFileDesc"),
          variant: "destructive"
        });
      }
    }, [toast, t]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile && isValidSpreadsheetFile(selectedFile)) {
        setFile(selectedFile);
      } else {
        toast({
          title: t("bulk.errors.invalidFile"),
          description: t("bulk.errors.invalidFileDesc"),
          variant: "destructive"
        });
      }
    };

    const isValidSpreadsheetFile = (file: File): boolean => {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv',
        'application/csv'
      ];
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      const maxSize = 10 * 1024 * 1024; // 10MB
      return (validTypes.includes(file.type) || hasValidExtension) && file.size <= maxSize;
    };

    const parseExcelToText = async (file: File): Promise<string> => {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const data: any[][] = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
      
      return data
        .filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== ''))
        .map(row => row.join(' | '))
        .join('\n');
    };

    const handleParse = async () => {
      if (!file || !parsedText) return;

      abortControllerRef.current = new AbortController();
      
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          abortControllerRef.current?.abort();
          reject(new Error("CLIENT_TIMEOUT"));
        }, CLIENT_TIMEOUT_MS);
      });
      
      try {
        const result = await Promise.race([
          processBulkOrders('excel', parsedText, (completed, total) => {
            onProgress?.({ completed, total });
          }),
          timeoutPromise
        ]);

        if (result.success && result.orders.length > 0) {
          onParsed(result.orders, result.summary);
          const successMsg = result.orders.length === 1 
            ? t("bulk.summary.success").replace("{{count}}", "1")
            : t("bulk.summary.successPlural").replace("{{count}}", String(result.orders.length));
          toast({
            title: t("common.success"),
            description: `${successMsg} (${(result.summary.processingTimeMs / 1000).toFixed(1)}s)`
          });
        } else {
          toast({
            title: t("bulk.errors.noOrders"),
            description: result.error || t("bulk.errors.extractionFailed"),
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Upload error:', error);
        
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        
        if (errorMessage === "CLIENT_TIMEOUT") {
          toast({
            title: t("bulk.errors.timeout"),
            description: t("bulk.errors.timeoutDesc"),
            variant: "destructive"
          });
        } else if (errorMessage.includes("RATE_LIMIT")) {
          toast({
            title: t("bulk.errors.rateLimit"),
            description: t("bulk.errors.rateLimitDesc"),
            variant: "destructive"
          });
        } else if (errorMessage.includes("CREDITS_REQUIRED")) {
          toast({
            title: t("bulk.errors.creditsRequired"),
            description: t("bulk.errors.creditsRequiredDesc"),
            variant: "destructive"
          });
        } else {
          toast({
            title: t("common.error"),
            description: t("bulk.errors.processingFailed"),
            variant: "destructive"
          });
        }
      } finally {
        abortControllerRef.current = null;
      }
    };

    const isLargeFile = file && file.size > LARGE_FILE_SIZE_BYTES;

    // Compact workbench layout matching SmartPasteTab
    return (
      <div className="flex h-full flex-col gap-3">
        {/* Constraints line at top - matches SmartPasteTab pattern */}
        <p className="text-xs text-muted-foreground">
          {t("bulk.constraintsExcel") || ".xlsx, .xls, .csv • Max 10MB"}
          {isLargeFile && (
            <span className="text-warning ml-2 inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 inline" />
              {t("bulk.largeFileWarning") || "May take longer"}
            </span>
          )}
        </p>

        {/* Compact file input - not a large dropzone */}
        <div
          className={`border-2 rounded-xl p-3 transition-colors bg-muted/20 ${
            dragActive ? 'border-primary bg-primary/5' : 'border-border'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {file ? (
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-5 w-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFile(null)}
              >
                {t("bulk.actions.remove") || "Remove"}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Upload className="h-5 w-5 text-muted-foreground shrink-0" />
              <p className="text-sm text-muted-foreground flex-1">
                {t("bulk.dropFile") || "Drop file here or choose"}
              </p>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="hidden"
                id="excel-upload"
              />
              <label htmlFor="excel-upload">
                <Button variant="secondary" size="sm" asChild>
                  <span>{t("bulk.chooseFile") || "Choose file"}</span>
                </Button>
              </label>
            </div>
          )}
        </div>

        {/* Drag indicator - appears only when dragging */}
        {dragActive && (
          <div className="flex items-center justify-center border-2 border-dashed border-primary/50 rounded-lg bg-primary/5 py-8">
            <p className="text-sm text-primary font-medium">
              {t("bulk.dropHere") || "Drop file here"}
            </p>
          </div>
        )}
      </div>
    );
  }
);

ExcelUploadTab.displayName = "ExcelUploadTab";
